import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowRight, CheckCircle, Copy, Check, RotateCcw } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { trackVisaFinderCompleted, trackVisaEmailCaptured } from '../../lib/analytics';
import { attachUtmToUser } from '../../lib/utm';
import {
  VISA_THRESHOLDS,
  approx,
  PER_APPLICANT_LABEL,
  STANDARD_DISCLAIMER,
  DEPENDENT_DISCLAIMER,
} from '../../content/visaThresholds';

const TR = VISA_THRESHOLDS.temporary_resident;
const PR = VISA_THRESHOLDS.permanent_resident;

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SITE_URL = 'https://mundalo.com';

// ============== QUIZ ==============
const QUESTIONS = [
  {
    id: 'q1',
    eyebrow: 'TELL US ABOUT YOUR SITUATION',
    prompt: 'Which best describes how you earn your income or why you are moving?',
    options: [
      { value: 'A', label: 'I work remotely for a foreign company or client' },
      { value: 'B', label: 'I am retired and receive pension or Social Security income' },
      { value: 'C', label: 'I am married to or have a Mexican citizen parent or child' },
      { value: 'D', label: 'I plan to invest in a Mexican business or property' },
      { value: 'E', label: 'I have a job offer from a Mexican company' },
      { value: 'F', label: 'I am a student enrolling in a Mexican school or university' },
      { value: 'G', label: 'I am freelance, self-employed, or earn investment income' },
      { value: 'H', label: 'I just want to try Mexico for a few months first' },
    ],
  },
  {
    id: 'q2',
    eyebrow: 'TELL US ABOUT YOUR INCOME',
    prompt: 'What is your monthly income from all sources combined?',
    options: [
      { value: 'A', label: 'Under $2,500 per month' },
      { value: 'B', label: '$2,500 to $4,400 per month' },
      { value: 'C', label: '$4,400 or more per month' },
      { value: 'D', label: 'I prefer to qualify through savings - I have $74,000 or more' },
      { value: 'E', label: 'I am not sure of the exact amount' },
    ],
  },
  {
    id: 'q3',
    eyebrow: 'TELL US ABOUT YOUR TIMELINE',
    prompt: 'How long are you planning to stay in Mexico?',
    options: [
      { value: 'A', label: 'Up to 6 months - I want to try it first' },
      { value: 'B', label: '1 to 4 years - I want legal temporary residency' },
      { value: 'C', label: 'Permanently - I want to live there indefinitely' },
      { value: 'D', label: 'I am not sure yet' },
    ],
  },
  {
    id: 'q4',
    eyebrow: 'TELL US ABOUT YOUR FAMILY',
    prompt: 'Who is making this move with you?',
    options: [
      { value: 'A', label: 'Just me' },
      { value: 'B', label: 'Me and my spouse or partner' },
      { value: 'C', label: 'Me and my children' },
      { value: 'D', label: 'Me, my partner, and our children' },
      { value: 'E', label: 'Me and a parent or other family member' },
    ],
  },
];

// ============== VISA DATA ==============
const VISAS = {
  tourist: {
    id: 'tourist',
    name: 'Tourist Permit - FMM',
    duration: 'Up to 180 days',
    cost: '$0 - free on arrival for US citizens',
    income: 'None',
    processing: 'Instant at port of entry',
    requirements: [
      'Valid US passport',
      'Return ticket or proof of funds',
      'Tourism form on arrival',
    ],
    note: 'Cannot work or establish residency. Does not lead to permanent status. If you want to stay beyond 180 days you must apply for Temporary Residency at a Mexican consulate in the US before entering.',
  },
  temporary: {
    id: 'temporary',
    name: 'Temporary Resident Visa',
    duration: '1 to 4 years - renewable annually up to 4 years',
    cost: '$56 consulate fee + $560 USD first-year card at INM in Mexico',
    income: `${approx(TR.monthly_income_usd)}/month ${PER_APPLICANT_LABEL}, or ${approx(TR.savings_usd)} in savings ${PER_APPLICANT_LABEL} - 2026 UMA-based INM figures. ${STANDARD_DISCLAIMER} ${DEPENDENT_DISCLAIMER}`,
    processing: '2 to 6 weeks at the consulate',
    requirements: [
      'Valid passport with 6+ months validity',
      '12 months of bank statements showing consistent income or savings',
      'Completed application form, passport photos, and criminal background check',
    ],
    note: 'The most common visa for Americans moving to Mexico. After 4 years as a Temporary Resident you are eligible for Permanent Residency. Social Security, pension, employer payroll, rental income, and investment distributions all qualify as income.',
  },
  retirement: {
    id: 'retirement',
    name: 'Retirement Visa - Temporary Residency for Pensioners',
    duration: '1 to 4 years - renewable annually up to 4 years',
    cost: '$56 consulate fee + $560 USD first-year card at INM in Mexico',
    income: `${approx(TR.monthly_income_usd)}/month ${PER_APPLICANT_LABEL}, or ${approx(TR.savings_usd)} in savings ${PER_APPLICANT_LABEL}. ${STANDARD_DISCLAIMER} ${DEPENDENT_DISCLAIMER}`,
    processing: '2 to 6 weeks at the consulate',
    requirements: [
      'Social Security award letter or pension statements (past 12 months)',
      'Valid passport with 6+ months validity',
      'Criminal background check',
    ],
    note: 'Pension and Social Security income qualifies directly. Bring your Social Security award letter or pension statements for the past 12 months. The income requirement is the same as any Temporary Resident applicant - Mexican immigration law does not publish a separate lower threshold for retirees. Verify with your specific consulate before applying.',
  },
  family: {
    id: 'family',
    name: 'Family Unity Visa - Temporary Residency',
    duration: '1 to 4 years - renewable',
    cost: '$56 consulate fee + $560 USD first-year card',
    income: `None if sponsored by a Mexican citizen. Standard ${approx(TR.monthly_income_usd)}/month ${PER_APPLICANT_LABEL} if sponsored by a permanent resident.`,
    processing: '2 to 6 weeks',
    requirements: [
      'Marriage certificate apostilled, or birth certificate showing relationship',
      'Legal affidavit from Mexican citizen or permanent resident sponsor',
      'Valid passport and criminal background check',
    ],
    note: 'Permanent Residency after 2 years instead of the standard 4 years when sponsored by a Mexican citizen. This is the fastest legal path to permanent status.',
  },
  employment: {
    id: 'employment',
    name: 'Employment Visa - Temporary Residency with Work Authorization',
    duration: '1 to 4 years - tied to employer',
    cost: '$56 consulate fee + $560 USD first-year card',
    income: 'Based on your job offer - no separate income threshold',
    processing: '2 to 8 weeks - employer manages most documentation',
    requirements: [
      'Job offer letter from Mexican employer',
      'Employment contract and employer sponsorship documentation',
      'Valid passport and criminal background check',
    ],
    note: 'You must work for the sponsoring employer. Changing jobs requires a new visa. Your employer initiates and manages most of the process.',
  },
  investment: {
    id: 'investment',
    name: 'Investment Visa - Temporary Residency for Investors',
    duration: '1 to 4 years',
    cost: '$56 consulate fee + $560 USD first-year card',
    income: 'Minimum $293,000 USD in a Mexican business OR minimum $586,000 USD in Mexican property',
    processing: '2 to 8 weeks',
    requirements: [
      'Business registration (RFC) and business plan in Spanish',
      'Proof of capital investment and articles of incorporation',
      'Valid passport and criminal background check',
    ],
    note: 'Allows you to work in Mexico. Can use an existing business or purchase Mexican property as the investment vehicle.',
  },
  student: {
    id: 'student',
    name: 'Student Visa - Temporary Residency for Students',
    duration: 'Duration of program - up to 4 years',
    cost: '$56 consulate fee + $560 USD first-year card',
    income: 'Proof of financial support for tuition plus living expenses',
    processing: '2 to 6 weeks',
    requirements: [
      'Acceptance letter from accredited Mexican institution',
      'Proof of enrollment and financial support',
      'Valid passport and criminal background check',
    ],
    note: 'Work is not permitted on this visa. Duration depends on the length of your program.',
  },
};

// ============== MATCHING LOGIC ==============
const matchVisas = (a) => {
  const { q1, q2, q3 } = a;
  const matches = []; // [{id, role, extra}]

  // Order matters - first match wins for "primary"
  if (q1 === 'H' || q3 === 'A') {
    matches.push({ id: 'tourist', role: 'primary' });
    if (q2 === 'C' || q2 === 'D') matches.push({ id: 'temporary', role: 'secondary' });
  } else if (q1 === 'C') {
    matches.push({ id: 'family', role: 'primary' });
    matches.push({ id: 'temporary', role: 'secondary' });
  } else if (q1 === 'E') {
    matches.push({ id: 'employment', role: 'primary' });
  } else if (q1 === 'F') {
    matches.push({ id: 'student', role: 'primary' });
  } else if (q1 === 'D') {
    matches.push({ id: 'investment', role: 'primary' });
  } else if (q1 === 'B' && (q2 === 'A' || q2 === 'B')) {
    matches.push({ id: 'retirement', role: 'primary' });
    matches.push({ id: 'temporary', role: 'secondary' });
  } else if (q1 === 'B' && (q2 === 'C' || q2 === 'D')) {
    matches.push({ id: 'temporary', role: 'primary', extra: `Pension and Social Security income qualifies directly toward the ${approx(TR.monthly_income_usd)}/month threshold ${PER_APPLICANT_LABEL}.` });
  } else if ((q1 === 'A' || q1 === 'G') && (q2 === 'C' || q2 === 'D')) {
    matches.push({ id: 'temporary', role: 'primary' });
  } else if ((q1 === 'A' || q1 === 'G') && q2 === 'B') {
    matches.push({ id: 'temporary', role: 'primary', extra: `Your income is near the threshold - most consulates want either ${approx(TR.savings_usd)} in savings OR 12 months of consistent income near the ${approx(TR.monthly_income_usd)}/month threshold ${PER_APPLICANT_LABEL}.` });
  } else if ((q1 === 'A' || q1 === 'G') && q2 === 'A') {
    matches.push({ id: 'tourist', role: 'primary', extra: `You do not yet meet the ${approx(TR.monthly_income_usd)}/month income or ${approx(TR.savings_usd)} savings requirement ${PER_APPLICANT_LABEL} for Temporary Residency. The Tourist Permit (180 days) is your starting point. To qualify for Temporary Residency, build up to either ${approx(TR.monthly_income_usd)}/month consistent income or ${approx(TR.savings_usd)} in savings.` });
  } else {
    matches.push({ id: 'temporary', role: 'primary' });
  }
  return matches;
};

// ============== UI BITS ==============
const ProgressBar = ({ current, total }) => (
  <div className="mb-8" data-testid="quiz-progress">
    <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#0A1F2E]/55 mb-2">
      Question {current} of {total}
    </p>
    <div className="flex gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1 flex-1 rounded-full ${i < current ? 'bg-[#E8A830]' : 'bg-[#0A1F2E]/10'}`}
        />
      ))}
    </div>
  </div>
);

const OptionCard = ({ value, label, selected, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`w-full text-left px-5 py-4 rounded-xl border-2 transition group ${
      selected
        ? 'bg-[#E8A830] border-[#E8A830] text-[#0A1F2E]'
        : 'bg-white border-[#E0D6B8] text-[#0A1F2E] hover:border-[#E8A830] hover:bg-[#E8A830]/5'
    }`}
  >
    <span className="flex items-center gap-3">
      <span
        className={`shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center font-mono text-xs font-semibold ${
          selected ? 'bg-[#0A1F2E] text-[#F9F6EF] border-[#0A1F2E]' : 'border-[#0A1F2E]/30 text-[#0A1F2E]/60 group-hover:border-[#E8A830]'
        }`}
      >
        {value}
      </span>
      <span className="text-base">{label}</span>
    </span>
  </button>
);

// ============== MAIN ==============
const VisaFinder = () => {
  const [searchParams] = useSearchParams();

  // Lazy initial state: read URL prefill once. Spec says `?q1=X` pre-answers
  // question 1 and begins the quiz at question 2.
  const initialQ1 = (() => {
    const v = searchParams.get('q1');
    return v && QUESTIONS[0].options.some((o) => o.value === v) ? v : null;
  })();
  const [answers, setAnswers] = useState(() => (initialQ1 ? { q1: initialQ1 } : {}));
  const [step, setStep] = useState(initialQ1 ? 1 : 0);
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);

  const onSelect = (qid, val) => {
    setAnswers((prev) => ({ ...prev, [qid]: val }));
    setHistory((h) => [...h, step]);
    setTimeout(() => setStep((s) => s + 1), 300);
  };

  const goBack = () => {
    setHistory((h) => {
      const next = [...h];
      const last = next.pop();
      if (typeof last === 'number') setStep(last);
      return next;
    });
  };

  const reset = () => {
    setAnswers({});
    setStep(0);
    setHistory([]);
    window.scrollTo(0, 0);
  };

  const showingResults = step >= QUESTIONS.length;
  const current = QUESTIONS[step];
  const matches = useMemo(() => (showingResults ? matchVisas(answers) : []), [showingResults, answers]);

  // iter 136 - fire GA4 visa_finder_completed when results render.
  // Deduped per (primary_visa + q1) within session inside the helper.
  useEffect(() => {
    if (showingResults && matches.length > 0) {
      const primary = matches.find((m) => m.role === 'primary');
      const primaryVisaId = primary?.id || matches[0].id;
      const primaryVisaName = VISAS[primaryVisaId]?.name || primaryVisaId;
      trackVisaFinderCompleted({
        primary_visa: primaryVisaName,
        q1_situation: answers.q1,
      });
    }
  }, [showingResults, matches, answers.q1]);

  const copyShareLink = async () => {
    const text = 'I just found out which Mexico visa I need with the free Mundalo Visa Finder. Find yours at mundalo.com/mexico-visa-finder';
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) { /* noop */ }
  };

  return (
    <div className="min-h-screen bg-[#F9F6EF] flex flex-col">
      <Helmet>
        <title>Which Mexico Visa Do I Need? Free Visa Finder 2026 | Mundalo</title>
        <meta name="description" content="Find your exact Mexico visa in 4 questions. Temporary residency, digital nomad, retirement, family unity - your personalized recommendation for 2026." />
        <link rel="canonical" href={`${SITE_URL}/mexico-visa-finder`} />
        <meta property="og:title" content="Mexico Visa Finder - Which Visa Do You Need? Free Tool by Mundalo" />
        <meta property="og:description" content="4 questions. Your exact visa recommendation. Income requirements, processing times, and key requirements - all verified for 2026." />
        <meta property="og:url" content={`${SITE_URL}/mexico-visa-finder`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Mexico Visa Finder - Free Tool by Mundalo" />
        <meta name="twitter:description" content="4 questions. Your exact visa recommendation." />
      </Helmet>

      <Header />

      <main className="flex-1">
        <section className="pt-16 sm:pt-24 pb-10 px-6 md:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <p className="font-mono uppercase text-[11px] tracking-[0.2em] text-[#2CA8A0] mb-4">
              Free Mundalo tool · 2026
            </p>
            <h1 className="font-display italic text-5xl sm:text-6xl md:text-7xl text-[#0A1F2E] leading-[1.04] mb-6">
              Which Mexico Visa Do You Need?
            </h1>
            <p className="text-base sm:text-lg text-[#0A1F2E]/75 leading-relaxed">
              Wondering which Mexico visa you need? Tourist permit, Temporary Residency, Retirement Visa, Family Unity, Investment, Employment, Student - there is a right answer for your situation, and most Americans pick the wrong one the first time. Answer 4 questions and get your exact match, verified against current 2026 INM requirements.
            </p>
          </div>
        </section>

        <section className="px-6 md:px-10 pb-20">
          <div className="max-w-2xl mx-auto">
            {!showingResults && current && (
              <div className="bg-white border border-[#E0D6B8] rounded-2xl p-6 sm:p-8 shadow-sm" data-testid="quiz-card">
                <ProgressBar current={step + 1} total={QUESTIONS.length} />
                <p className="font-mono uppercase text-[11px] tracking-[0.18em] text-[#2CA8A0] mb-2" data-testid="question-eyebrow">
                  {current.eyebrow}
                </p>
                <h2 className="font-display italic text-3xl sm:text-4xl text-[#0A1F2E] leading-tight mb-6" data-testid="question-prompt">
                  {current.prompt}
                </h2>
                <div className="space-y-3">
                  {current.options.map((opt) => (
                    <OptionCard
                      key={opt.value}
                      value={opt.value}
                      label={opt.label}
                      selected={answers[current.id] === opt.value}
                      onClick={() => onSelect(current.id, opt.value)}
                      testId={`option-${current.id}-${opt.value}`}
                    />
                  ))}
                </div>
                {step > 0 && (
                  <button
                    type="button"
                    onClick={goBack}
                    data-testid="back-button"
                    className="mt-6 inline-flex items-center gap-2 text-sm text-[#0A1F2E]/65 hover:text-[#0A1F2E] transition"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                )}
              </div>
            )}

            {showingResults && matches.length > 0 && (
              <div className="space-y-6" data-testid="results-section">
                {matches.map((m, idx) => (
                  <VisaCard key={m.id} match={m} index={idx} />
                ))}

                <div className="bg-[#0A1F2E] text-[#F9F6EF] rounded-2xl p-8 sm:p-10" data-testid="conversion-card">
                  <p className="font-mono uppercase text-[11px] tracking-[0.2em] text-[#2CA8A0] mb-3">YOUR NEXT STEP</p>
                  <h3 className="font-display italic text-3xl sm:text-4xl mb-5 leading-tight">
                    Your Blueprint includes your complete visa roadmap.
                  </h3>
                  <p className="text-base sm:text-lg leading-relaxed text-[#F9F6EF]/85 mb-7">
                    Your visa section is built for your specific situation - your income sources, your chosen city&apos;s INM office, the current requirements at the consulate nearest to you in the US, and the exact document checklist you need to bring to your appointment.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                    <Link
                      to="/relocation/blueprint"
                      data-testid="primary-cta"
                      className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#E8A830] text-[#0A1F2E] font-mono uppercase text-xs tracking-[0.14em] rounded-full font-semibold hover:bg-[#f0b340] transition"
                    >
                      Get Your Blueprint - From $199
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link
                      to="/relo-news/mexico-temporary-residency-requirements-2026"
                      data-testid="secondary-cta"
                      className="text-sm text-[#F9F6EF]/75 hover:text-[#F9F6EF] underline underline-offset-4"
                    >
                      Learn more about Mexico residency
                    </Link>
                  </div>
                </div>

                <VisaEmailCapture matches={matches} answers={answers} />

                <div className="text-center pt-2">
                  <p className="text-sm text-[#0A1F2E]/65 mb-3">Share your result</p>
                  <button
                    type="button"
                    onClick={copyShareLink}
                    data-testid="copy-share-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-[#0A1F2E]/20 rounded-full text-sm text-[#0A1F2E] hover:border-[#2CA8A0] hover:text-[#2CA8A0] transition"
                  >
                    {copied ? <Check className="w-4 h-4 text-[#2CA8A0]" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy link'}
                  </button>
                </div>

                <div className="text-center">
                  <button onClick={reset} className="inline-flex items-center gap-1.5 text-sm text-[#0A1F2E]/65 hover:text-[#0A1F2E]" data-testid="retake-btn">
                    <RotateCcw className="w-3.5 h-3.5" /> Retake the assessment
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {!showingResults && <SEOContent />}
      </main>

      <Footer />
    </div>
  );
};

// ============== EMAIL CAPTURE (iter 139) ==============
// Always renders below the conversion CTA. Submits to /api/tools/save-visa-result
// and persists the success state for the session by primary-visa name.
const VISA_EMAIL_SUBMITTED_KEY = (primary) => `tool_visa_submitted:${primary}`;

const VisaEmailCapture = ({ matches, answers }) => {
  const primary = matches.find((m) => m.role === 'primary') || matches[0];
  const primaryVisaId = primary?.id;
  const primaryVisa = VISAS[primaryVisaId];

  const submittedAlready = (() => {
    if (!primaryVisa) return false;
    try {
      return typeof window !== 'undefined'
        && window.sessionStorage.getItem(VISA_EMAIL_SUBMITTED_KEY(primaryVisa.name)) === '1';
    } catch { return false; }
  })();

  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(submittedAlready);
  const [sending, setSending] = useState(false);

  if (!primaryVisa) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) return;
    setSending(true);
    try {
      const cards = matches.map((m) => {
        const v = VISAS[m.id];
        return v ? {
          name: v.name,
          duration: v.duration,
          cost: v.cost,
          income: v.income,
          processing: v.processing,
          requirements: v.requirements,
          note: m.extra || v.note,
          role: m.role,
        } : null;
      }).filter(Boolean);

      const r = await fetch(`${API_URL}/api/tools/save-visa-result`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          primary_visa: primaryVisa.name,
          q1_situation: answers.q1 || null,
          q2_income: answers.q2 || null,
          q3_timeline: answers.q3 || null,
          cards,
        }),
      });
      if (r.ok) {
        setEmailSent(true);
        try { window.sessionStorage.setItem(VISA_EMAIL_SUBMITTED_KEY(primaryVisa.name), '1'); } catch { /* sessionStorage blocked */ }
        trackVisaEmailCaptured({ primary_visa: primaryVisa.name });
        // iter 144h - first-touch UTM attribution on the lead row.
        attachUtmToUser({ email: email.trim().toLowerCase(), useAuth: false });
      }
    } catch (err) {
      /* silent - user can retry */
    } finally {
      setSending(false);
    }
  };

  if (emailSent) {
    return (
      <div
        data-testid="visa-email-sent-confirm"
        className="bg-[#2CA8A0]/10 border-2 border-[#2CA8A0] rounded-2xl p-6 text-center"
      >
        <Check className="w-6 h-6 text-[#2CA8A0] mx-auto mb-2" />
        <p className="font-mono uppercase text-xs tracking-[0.18em] text-[#2CA8A0]">Sent. Check your inbox.</p>
      </div>
    );
  }

  return (
    <div
      data-testid="visa-email-capture"
      className="bg-[#F9F6EF] border border-[#0A1F2E]/15 rounded-2xl p-6 sm:p-7"
    >
      <p className="font-display italic text-2xl sm:text-3xl text-[#0A1F2E] mb-2 leading-tight">
        You have your answer. Where should we send it?
      </p>
      <p className="text-sm text-[#0A1F2E]/65 mb-4">
        Your visa recommendation with the exact documents you need and your step-by-step application timeline.
      </p>
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          required
          data-testid="visa-email-input"
          className="flex-1 px-4 py-3 border border-[#E0D6B8] rounded-lg text-[#0A1F2E] bg-white focus:outline-none focus:border-[#2CA8A0]"
        />
        <button
          type="submit"
          disabled={sending}
          data-testid="visa-email-submit"
          className="px-5 py-3 bg-[#E8A830] text-[#0A1F2E] font-mono uppercase text-xs tracking-[0.14em] rounded-full font-semibold hover:bg-[#f0b340] transition disabled:opacity-50"
        >
          {sending ? 'Sending…' : 'Send My Roadmap'}
        </button>
      </form>
      <p className="mt-3 text-xs text-[#0A1F2E]/55 italic">
        Planning this move with someone? Forward them your visa recommendation.
      </p>
    </div>
  );
};


// ============== VISA CARD ==============
const VisaCard = ({ match, index }) => {
  const v = VISAS[match.id];
  if (!v) return null;
  const isPrimary = match.role === 'primary';
  return (
    <div
      data-testid={`visa-card-${v.id}`}
      className={`rounded-2xl border-2 p-6 sm:p-8 bg-white ${
        isPrimary ? 'border-[#2CA8A0]' : 'border-[#0A1F2E]/15'
      }`}
    >
      <p
        className={`font-mono uppercase text-[10px] tracking-[0.2em] mb-2 ${
          isPrimary ? 'text-[#2CA8A0]' : 'text-[#5A6B73]'
        }`}
      >
        {isPrimary ? 'Best match for your situation' : 'Alternative option'}
      </p>
      <h3 className="font-display italic text-3xl sm:text-4xl text-[#0A1F2E] leading-tight mb-5">
        {v.name}
      </h3>

      <dl className="space-y-3 text-sm sm:text-base">
        <div className="grid grid-cols-1 sm:grid-cols-[160px,1fr] gap-1 sm:gap-4">
          <dt className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 pt-0.5">Good for</dt>
          <dd className="text-[#0A1F2E]">{v.duration}</dd>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[160px,1fr] gap-1 sm:gap-4">
          <dt className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 pt-0.5">Cost</dt>
          <dd className="text-[#0A1F2E]">{v.cost}</dd>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[160px,1fr] gap-1 sm:gap-4">
          <dt className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 pt-0.5">Income / investment</dt>
          <dd className="text-[#0A1F2E]">{v.income}</dd>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[160px,1fr] gap-1 sm:gap-4">
          <dt className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 pt-0.5">Processing</dt>
          <dd className="text-[#0A1F2E]">{v.processing}</dd>
        </div>
      </dl>

      <div className="mt-5 pt-5 border-t border-[#F4ECD8]">
        <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 mb-3">Key requirements</p>
        <ul className="space-y-2">
          {v.requirements.map((req, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-[#0A1F2E]">
              <CheckCircle className="w-4 h-4 text-[#2CA8A0] mt-0.5 shrink-0" />
              <span>{req}</span>
            </li>
          ))}
        </ul>
      </div>

      <p className="mt-5 text-sm italic text-[#0A1F2E]/70 leading-relaxed">
        {match.extra || v.note}
      </p>
    </div>
  );
};

// ============== SEO CONTENT ==============
const SEOContent = () => (
  <section className="px-6 md:px-10 pb-24" data-testid="seo-content">
    <div className="max-w-3xl mx-auto">
      <h2 className="font-display italic text-3xl sm:text-4xl text-[#0A1F2E] mb-5">
        Mexico Visa Types for Americans in 2026 - What You Need to Know
      </h2>
      <p className="text-[#0A1F2E]/80 leading-relaxed mb-4">
        Mexico does not have a single &ldquo;expat visa.&rdquo; There is a constellation of options - Tourist Permit (FMM), Temporary Resident Visa, Retirement Visa, Family Unity Visa, Employment Visa, Investment Visa, and Student Visa - and the right one for you depends almost entirely on how you earn your money, who is moving with you, and how long you intend to stay. Choosing wrong means either getting denied at the consulate or finding yourself trapped on the wrong visa six months in.
      </p>
      <p className="text-[#0A1F2E]/80 leading-relaxed mb-4">
        The most common mistake Americans make is treating the Tourist Permit as a residency path. It is not. The 180-day FMM is for tourism, period - no work, no residency, and after six months you are required to leave. Americans who try to &ldquo;border hop&rdquo; by leaving and re-entering risk being denied entry by INM, who track these patterns. If you want to actually live in Mexico, you need a Temporary Resident Visa from the start, applied for at a Mexican consulate <em>in the US</em> before you move. You cannot apply from inside Mexico.
      </p>
      <p className="text-[#0A1F2E]/80 leading-relaxed mb-4">
        The second most common mistake: assuming all Mexican consulates treat the financial requirements the same way. They do not. The published 2026 INM figure for Temporary Residency is {approx(TR.monthly_income_usd)}/month or {approx(TR.savings_usd)} in savings {PER_APPLICANT_LABEL} - but each consulate has discretion, and the consulates in cities like Dallas, Houston, Phoenix, and Los Angeles process many more US applicants than smaller ones and tend to be stricter on documentation. Some consulates accept slightly lower retirement income with strong savings, others do not. {STANDARD_DISCLAIMER} {DEPENDENT_DISCLAIMER} The Mundalo Blueprint maps the specific tendencies of the consulate nearest to you - because your application happens at one specific consulate, not &ldquo;in general.&rdquo;
      </p>
      <p className="text-[#0A1F2E]/80 leading-relaxed mb-4">
        Family Unity is the fastest legal path to permanent status for Americans married to or directly related to a Mexican citizen - Permanent Residency in 2 years instead of 4. Investment Visa is the path for entrepreneurs willing to put approximately $293,000 into a Mexican business or approximately $586,000 into property. Employment Visa is for the rare American with a Mexican job offer - your employer manages most of it. Student Visa is the path for those enrolled in an accredited Mexican school or university for the duration of the program.
      </p>
      <p className="text-[#0A1F2E]/80 leading-relaxed">
        Want the full breakdown of Temporary Residency requirements?{' '}
        <Link to="/relo-news/mexico-temporary-residency-requirements-2026" className="text-[#2CA8A0] underline underline-offset-2">Read our 2026 guide</Link>
        {' '}or follow our complete{' '}
        <Link to="/relo-news/moving-to-mexico-checklist-2026" className="text-[#2CA8A0] underline underline-offset-2">Moving to Mexico Checklist</Link>.
      </p>
    </div>
  </section>
);

export default VisaFinder;
